import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "row",
    "search",
    "pageSize",
    "info",
    "previousButton",
    "nextButton",
    "emptyState",
    "pageLabel"
  ]

  static values = {
    pageSize: { type: Number, default: 10 },
    filename: { type: String, default: "feeds-export.csv" },
    columns: { type: Array, default: ["Name", "RSS Link", "Categories"] }
  }

  connect() {
    this.currentPage = 1
    this.rows = this.rowTargets
    this.render()
  }

  search() {
    this.currentPage = 1
    this.render()
  }

  clearSearch() {
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.currentPage = 1
    this.render()
    this.searchTarget?.focus()
  }

  changePageSize() {
    this.pageSizeValue = Number(this.pageSizeTarget.value) || 10
    this.currentPage = 1
    this.render()
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage -= 1
      this.render()
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1
      this.render()
    }
  }

  exportCsv() {
    const rows = this.filteredRows().map((row) => this.csvValues(row))
    const csv = [this.columnsValue, ...rows]
      .map((row) => row.map((value) => this.csvCell(value)).join(","))
      .join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = this.filenameValue
    link.style.display = "none"
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 100)
  }

  render() {
    const filteredRows = this.filteredRows()
    const pageSize = this.pageSizeValue || 10
    this.totalPages = Math.max(Math.ceil(filteredRows.length / pageSize), 1)
    this.currentPage = Math.min(this.currentPage, this.totalPages)
    const start = (this.currentPage - 1) * pageSize
    const end = start + pageSize
    const visibleRows = new Set(filteredRows.slice(start, end))

    this.rows.forEach((row) => {
      row.hidden = !visibleRows.has(row)
    })

    this.emptyStateTarget.hidden = filteredRows.length > 0
    this.previousButtonTarget.disabled = this.currentPage <= 1
    this.nextButtonTarget.disabled = this.currentPage >= this.totalPages
    this.infoTarget.textContent = this.infoText(filteredRows.length, start, Math.min(end, filteredRows.length))
    if (this.hasPageLabelTarget) {
      this.pageLabelTarget.textContent = `Page ${this.currentPage} of ${this.totalPages}`
    }
  }

  filteredRows() {
    const term = this.searchTarget.value.trim().toLowerCase()
    if (term.length === 0) return this.rows

    return this.rows.filter((row) => (row.dataset.searchText || "").includes(term))
  }

  infoText(total, start, end) {
    if (total === 0) return "Showing 0 entries"

    return `Showing ${start + 1} to ${end} of ${total} entries`
  }

  csvValues(row) {
    const raw = row.dataset.csvValues
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
      } catch (_error) {
        // Fall through to legacy attributes.
      }
    }

    return [
      row.dataset.csvName || "",
      row.dataset.csvRss || "",
      row.dataset.csvCategories || ""
    ]
  }

  csvCell(value) {
    return `"${String(value).replaceAll("\"", "\"\"")}"`
  }
}
